import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { clock, dayMonth } from "../../components/meetings/MeetingRow";
import { cn } from "../../components/ui/cn";
import {
  fieldApi,
  fieldKeys,
  ukToday,
  type FieldHome as FieldHomeData,
  type FieldMeetingBrief,
} from "../../lib/field-api";
import { formatInZone } from "../../lib/time";
import type { User } from "../../types";
import { FieldTabs } from "./FieldShell";

/**
 * Field Sales' Home: what needs doing, readable in five seconds.
 *
 * Every card is one number, one line of context and a way through to the list
 * behind it. A card that is asking for something -- meetings not written up,
 * follow-ups not sent, tasks gone overdue -- is amber; one with nothing to do
 * shows a quiet tick rather than a celebratory zero, because the page is for
 * finding work and an empty card should get out of the way.
 *
 * Read fresh every time the tab is opened, and every five minutes while it
 * stays open: it is the page a rep leaves up on a phone between visits.
 */

const REFRESH_MS = 5 * 60_000;

export function FieldHome({ user }: { user: User }) {
  const home = useQuery({
    queryKey: fieldKeys.home,
    queryFn: fieldApi.home,
    refetchOnMount: "always",
    refetchInterval: REFRESH_MS,
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <FieldTabs user={user} tab="home" />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-4 py-5 sm:px-6">
          <Greeting user={user} />

          {home.isError ? (
            <p className="mt-6 rounded-lg border border-line bg-surface-2 p-4 text-sm text-muted">
              Could not load your summary.{" "}
              <button
                type="button"
                onClick={() => void home.refetch()}
                className="text-brand-600 hover:underline dark:text-brand-400"
              >
                Try again
              </button>
            </p>
          ) : (
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {home.data ? <Cards data={home.data} /> : <Skeletons />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Greeting({ user }: { user: User }) {
  const now = new Date();
  const hour = Number(formatInZone(now, { hour: "2-digit", hour12: false }));
  const part = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  const first = user.name.trim().split(/\s+/)[0] ?? user.name;

  return (
    <header>
      <h1 className="text-xl font-semibold text-content">
        Good {part}, {first}
      </h1>
      <p className="mt-0.5 text-sm text-muted">
        {formatInZone(now, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
      </p>
    </header>
  );
}

function Cards({ data }: { data: FieldHomeData }) {
  const { meetings, tasks, note } = data;
  const next = meetings.week.next;

  return (
    <>
      <Card
        to="/meetings?view=week"
        value={meetings.week.count}
        label={meetings.week.count === 1 ? "Meeting this week" : "Meetings this week"}
        context={
          next
            ? `Next: ${dayMonth(next.startAt).split(" ")[0]} ${clock(next.startAt)}, ${next.companyName}`
            : meetings.week.count > 0
              ? "Nothing else this week"
              : "Nothing booked with customers"
        }
      />

      <Card
        to="/meetings?view=recent&filter=needs_notes"
        value={meetings.needsNotes}
        label="Meetings without notes"
        attention={meetings.needsNotes > 0}
        context={
          meetings.needsNotes > 0
            ? "Write them up while you remember"
            : "All written up"
        }
      />

      <Card
        to="/meetings?view=recent&filter=needs_follow_up"
        value={meetings.needsFollowUp}
        label="Follow-ups to send"
        attention={meetings.needsFollowUp > 0}
        context={
          meetings.needsFollowUp > 0
            ? "Written up, but the customer hasn't heard from you"
            : "Everyone's had their follow-up"
        }
      />

      <TasksCard tasks={tasks} />

      <TodayCard meetings={meetings.today} />

      <NoteCard note={note} />
    </>
  );
}

/**
 * One summary card: big number, label, a line of context, and an arrow.
 *
 * The whole card is the link. A rep on a phone hits it with a thumb, and a
 * card whose only target is a small arrow in the corner is one they miss.
 *
 * On a phone the same card lies on its side: the number in a column of its
 * own at the left, the label and its context beside it, one row. Stacked, as
 * on a wider screen, each card was 116px tall and the four counts filled the
 * whole screen before today's meetings were reached -- one column, as it
 * should be, but a column of mostly padding. The numbers line up down the
 * left edge, so the screen still reads at a glance.
 */
function Card({
  to,
  value,
  label,
  context,
  attention = false,
  quiet = false,
  children,
}: {
  to: string;
  value: number | null;
  label: string;
  context: React.ReactNode;
  /** Something here is asking to be done. */
  attention?: boolean;
  /** The number is not meaningful -- Lane not connected, say. */
  quiet?: boolean;
  children?: React.ReactNode;
}) {
  const settled = !attention && !quiet && value === 0;
  const body = (
    <>
      <div className="flex items-center gap-3 sm:items-start">
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:block">
          <div className="flex w-12 shrink-0 items-baseline gap-1 sm:w-auto sm:gap-2">
            <span
              className={cn(
                "text-2xl font-semibold tabular-nums sm:text-3xl",
                attention ? "text-amber-700 dark:text-amber-400" : quiet ? "text-faint" : "text-content",
              )}
            >
              {value ?? "—"}
            </span>
            {settled ? (
              <span
                aria-label="Nothing to do"
                className="text-sm text-emerald-600 dark:text-emerald-400"
              >
                ✓
              </span>
            ) : null}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-content sm:mt-0.5">{label}</p>
            <div className="truncate text-xs text-muted sm:hidden">{context}</div>
          </div>
        </div>
        <span
          aria-hidden
          className="text-lg text-faint transition-colors group-hover:text-brand-600 sm:mt-1 dark:group-hover:text-brand-400"
        >
          →
        </span>
      </div>
      <div className="mt-2 hidden truncate text-xs text-muted sm:block">{context}</div>
      {children}
    </>
  );

  const className = cn(
    "group block rounded-xl border border-l-4 bg-surface p-3 transition-colors hover:bg-surface-2 sm:p-4",
    attention
      ? "border-amber-300 border-l-amber-500 dark:border-amber-500/40"
      : "border-line border-l-line",
  );

  return (
    <Link to={to} className={className}>
      {body}
    </Link>
  );
}

function TasksCard({ tasks }: { tasks: FieldHomeData["tasks"] }) {
  if (!tasks.enabled || tasks.error) {
    return (
      <Card
        to="/field/tasks"
        value={null}
        quiet
        label="Pending tasks"
        context={tasks.error ? "Lane didn't answer — try again shortly" : "Lane isn't connected on this server"}
      />
    );
  }

  return (
    <Card
      to="/field/tasks"
      value={tasks.open}
      label={tasks.open === 1 ? "Pending task" : "Pending tasks"}
      attention={tasks.overdue > 0}
      context={
        tasks.overdue > 0 ? (
          <span>
            <strong className="font-medium text-amber-700 dark:text-amber-400">
              {tasks.overdue} overdue
            </strong>
            {tasks.next ? ` · ${tasks.next.title}` : ""}
          </span>
        ) : tasks.next ? (
          `Next: ${tasks.next.title}`
        ) : (
          "Nothing on your plate"
        )
      }
    />
  );
}

/**
 * Today's meetings as a short list, each one a way into it.
 *
 * Not a single link like the others, because the useful click is "the 2pm",
 * not "my day". The card's own arrow still opens the day view.
 */
function TodayCard({ meetings }: { meetings: FieldMeetingBrief[] }) {
  const now = Date.now();
  const shown = meetings.slice(0, 4);

  return (
    <div className="flex flex-col rounded-xl border border-l-4 border-line border-l-line bg-surface p-3 sm:p-4">
      {/* Laid out as `Card` is, on its side on a phone. */}
      <Link to="/meetings?view=day" className="group flex items-center gap-3 sm:items-start">
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:block">
          <div className="flex w-12 shrink-0 items-baseline gap-2 sm:w-auto">
            <span className="text-2xl font-semibold tabular-nums text-content sm:text-3xl">
              {meetings.length}
            </span>
          </div>
          <p className="min-w-0 flex-1 text-sm font-medium text-content sm:mt-0.5">
            {meetings.length === 1 ? "Meeting today" : "Today's meetings"}
          </p>
        </div>
        <span
          aria-hidden
          className="text-lg text-faint sm:mt-1 transition-colors group-hover:text-brand-600 dark:group-hover:text-brand-400"
        >
          →
        </span>
      </Link>

      {shown.length === 0 ? (
        <p className="mt-2 text-xs text-muted">Nothing in the diary today</p>
      ) : (
        <ul className="-mx-2 mt-1.5">
          {shown.map((meeting) => {
            const over = new Date(meeting.endAt ?? meeting.startAt).getTime() < now;
            return (
              <li key={meeting.id}>
                <Link
                  to={`/meetings/${meeting.id}?view=day`}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-2 py-1 text-xs hover:bg-surface-2",
                    over ? "text-faint" : "text-content",
                  )}
                >
                  <span className="w-10 shrink-0 tabular-nums">{clock(meeting.startAt)}</span>
                  <span className="min-w-0 flex-1 truncate">
                    {meeting.companyName}
                    {meeting.internal ? <span className="text-faint"> · internal</span> : null}
                  </span>
                </Link>
              </li>
            );
          })}
          {meetings.length > shown.length ? (
            <li className="px-2 pt-0.5 text-[11px] text-faint">
              +{meetings.length - shown.length} more
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}

function NoteCard({ note }: { note: FieldHomeData["note"] }) {
  const empty = note.lines.length === 0;
  return (
    <Link
      to={`/field/notes/${ukToday()}`}
      className="group flex flex-col rounded-xl border border-l-4 border-line border-l-line bg-surface p-3 transition-colors hover:bg-surface-2 sm:p-4"
    >
      <div className="flex items-start gap-3">
        <p className="min-w-0 flex-1 text-sm font-medium text-content">Today's quick note</p>
        <span
          aria-hidden
          className="text-lg text-faint transition-colors group-hover:text-brand-600 dark:group-hover:text-brand-400"
        >
          →
        </span>
      </div>
      {empty ? (
        <p className="mt-2 text-sm text-brand-600 dark:text-brand-400">Start today's note</p>
      ) : (
        <ul className="mt-1.5 space-y-0.5">
          {note.lines.map((line, index) => (
            <li key={index} className="truncate text-xs text-muted">
              {line}
            </li>
          ))}
        </ul>
      )}
    </Link>
  );
}

function Skeletons() {
  return (
    <>
      {Array.from({ length: 6 }, (_, index) => (
        <div
          key={index}
          className="h-16 animate-pulse rounded-xl border border-line bg-surface-2 sm:h-[7.5rem]"
        />
      ))}
    </>
  );
}
